/**
 * Benchmark page comparing JSON vs Protobuf loading/parsing performance
 */

import protobuf from 'protobufjs';
import { qud } from '../proto/blueprints.js';

// WASM module - loaded dynamically
let wasmModule: {
  decode_blueprints: (data: Uint8Array) => number;
} | null = null;

// Inline proto definition for browser (avoiding file load)
const PROTO_DEF = `
syntax = "proto3";
package qud;

message PartParam {
  string key = 1;
  string value = 2;
}

message PartDef {
  string name = 1;
  repeated PartParam params = 2;
}

message StatEntry {
  string key = 1;
  int32 value = 2;
}

message TagEntry {
  string key = 1;
  string value = 2;
}

message RawBlueprintDef {
  string inherits = 1;
  repeated PartDef parts = 2;
  repeated PartDef builders = 3;
  repeated TagEntry tags = 4;
  repeated StatEntry stats = 5;
  repeated string remove_parts = 6;
  repeated string remove_builders = 7;
}

message BlueprintEntry {
  string name = 1;
  RawBlueprintDef def = 2;
}

message BlueprintData {
  repeated BlueprintEntry blueprints = 1;
}
`;

interface BenchmarkResult {
  name: string;
  fetchTime: number;
  parseTime: number;
  totalTime: number;
  itemCount: number;
  dataSize: number;
}

export async function renderProtoBenchmarkPage(appElement: HTMLDivElement): Promise<void> {
  appElement.innerHTML = `
    <style>
      .benchmark-container {
        padding: 20px;
        font-family: monospace;
        background: #1a1a2e;
        color: #eee;
        min-height: 100vh;
      }
      h1 { color: #00ff88; margin-bottom: 20px; }
      .controls { margin-bottom: 20px; }
      .run-btn {
        padding: 12px 24px;
        background: #00ff88;
        border: none;
        border-radius: 4px;
        color: #1a1a2e;
        font-family: monospace;
        font-size: 1rem;
        font-weight: bold;
        cursor: pointer;
        margin-right: 10px;
      }
      .run-btn:hover { background: #00cc6a; }
      .run-btn:disabled { background: #444; color: #888; cursor: not-allowed; }
      .iterations-input {
        padding: 8px;
        background: #0f3460;
        border: 1px solid #1a1a4e;
        border-radius: 4px;
        color: #eee;
        font-family: monospace;
        width: 80px;
      }
      .results {
        background: #16213e;
        border: 1px solid #0f3460;
        border-radius: 4px;
        padding: 20px;
        margin-top: 20px;
      }
      .result-row {
        display: flex;
        gap: 20px;
        padding: 10px 0;
        border-bottom: 1px solid #0f3460;
      }
      .result-row:last-child { border-bottom: none; }
      .result-label { width: 150px; color: #888; }
      .result-value { color: #00ff88; font-weight: bold; }
      .result-value.slower { color: #ff6b6b; }
      table {
        width: 100%;
        border-collapse: collapse;
        margin-top: 20px;
      }
      th, td {
        padding: 12px;
        text-align: left;
        border-bottom: 1px solid #0f3460;
      }
      th { color: #888; }
      .status { margin-top: 10px; color: #888; min-height: 20px; }
      .comparison {
        margin-top: 20px;
        padding: 15px;
        background: #0f3460;
        border-radius: 4px;
      }
      .comparison h3 { color: #00ff88; margin-bottom: 10px; }
    </style>
    <div class="benchmark-container">
      <h1>Protobuf vs JSON Benchmark</h1>
      <div class="controls">
        <button class="run-btn" id="run-btn">Run Benchmark</button>
        <label>
          Iterations:
          <input type="number" class="iterations-input" id="iterations" value="10" min="1" max="100">
        </label>
      </div>
      <div class="status" id="status"></div>
      <div class="results" id="results">
        <p>Click "Run Benchmark" to compare JSON vs Protobuf parsing performance.</p>
        <p style="color: #888; margin-top: 10px;">
          This tests:<br>
          1. Fetch time (network/cache)<br>
          2. Parse time (JSON.parse vs protobuf decode)<br>
          3. Total time to get usable data
        </p>
      </div>
    </div>
  `;

  const runBtn = document.getElementById('run-btn') as HTMLButtonElement;
  const iterationsInput = document.getElementById('iterations') as HTMLInputElement;
  const statusEl = document.getElementById('status') as HTMLDivElement;
  const resultsEl = document.getElementById('results') as HTMLDivElement;

  // Parse proto definition once
  const root = protobuf.parse(PROTO_DEF).root;
  const BlueprintData = root.lookupType('qud.BlueprintData');

  async function benchmarkJSON(iterations: number): Promise<BenchmarkResult[]> {
    const results: BenchmarkResult[] = [];

    for (let i = 0; i < iterations; i++) {
      // Clear cache by adding cache-busting param
      const url = `/data/blueprints.json?_=${Date.now()}-${i}`;

      const fetchStart = performance.now();
      const response = await fetch(url);
      const text = await response.text();
      const fetchEnd = performance.now();

      const parseStart = performance.now();
      const data = JSON.parse(text);
      const parseEnd = performance.now();

      results.push({
        name: 'JSON',
        fetchTime: fetchEnd - fetchStart,
        parseTime: parseEnd - parseStart,
        totalTime: fetchEnd - fetchStart + (parseEnd - parseStart),
        itemCount: Object.keys(data.blueprints).length,
        dataSize: text.length,
      });
    }

    return results;
  }

  async function benchmarkProtoReflection(iterations: number): Promise<BenchmarkResult[]> {
    const results: BenchmarkResult[] = [];

    for (let i = 0; i < iterations; i++) {
      const url = `/data/blueprints.pb?_=${Date.now()}-${i}`;

      const fetchStart = performance.now();
      const response = await fetch(url);
      const buffer = await response.arrayBuffer();
      const fetchEnd = performance.now();

      const parseStart = performance.now();
      const data = BlueprintData.decode(new Uint8Array(buffer)) as unknown as {
        blueprints: unknown[];
      };
      const parseEnd = performance.now();

      results.push({
        name: 'Proto (reflection)',
        fetchTime: fetchEnd - fetchStart,
        parseTime: parseEnd - parseStart,
        totalTime: fetchEnd - fetchStart + (parseEnd - parseStart),
        itemCount: data.blueprints.length,
        dataSize: buffer.byteLength,
      });
    }

    return results;
  }

  async function benchmarkProtoStatic(iterations: number): Promise<BenchmarkResult[]> {
    const results: BenchmarkResult[] = [];

    for (let i = 0; i < iterations; i++) {
      const url = `/data/blueprints.pb?_=${Date.now()}-${i}`;

      const fetchStart = performance.now();
      const response = await fetch(url);
      const buffer = await response.arrayBuffer();
      const fetchEnd = performance.now();

      const parseStart = performance.now();
      const data = qud.BlueprintData.decode(new Uint8Array(buffer));
      const parseEnd = performance.now();

      results.push({
        name: 'Proto (static)',
        fetchTime: fetchEnd - fetchStart,
        parseTime: parseEnd - parseStart,
        totalTime: fetchEnd - fetchStart + (parseEnd - parseStart),
        itemCount: data.blueprints.length,
        dataSize: buffer.byteLength,
      });
    }

    return results;
  }

  async function benchmarkProtoWasm(iterations: number): Promise<BenchmarkResult[]> {
    const results: BenchmarkResult[] = [];

    for (let i = 0; i < iterations; i++) {
      const url = `/data/blueprints.pb?_=${Date.now()}-${i}`;

      const fetchStart = performance.now();
      const response = await fetch(url);
      const buffer = await response.arrayBuffer();
      const fetchEnd = performance.now();

      const parseStart = performance.now();
      const count = wasmModule!.decode_blueprints(new Uint8Array(buffer));
      const parseEnd = performance.now();

      results.push({
        name: 'Proto (WASM/Rust)',
        fetchTime: fetchEnd - fetchStart,
        parseTime: parseEnd - parseStart,
        totalTime: fetchEnd - fetchStart + (parseEnd - parseStart),
        itemCount: count,
        dataSize: buffer.byteLength,
      });
    }

    return results;
  }

  function min(results: BenchmarkResult[], key: keyof BenchmarkResult): number {
    return Math.min(...results.map((r) => r[key] as number));
  }

  function formatMs(ms: number): string {
    return ms.toFixed(2) + ' ms';
  }

  function formatSize(bytes: number): string {
    return (bytes / 1024 / 1024).toFixed(2) + ' MB';
  }

  runBtn.addEventListener('click', async () => {
    const iterations = parseInt(iterationsInput.value, 10) || 10;

    runBtn.disabled = true;
    statusEl.textContent = 'Running benchmark...';

    try {
      // Load WASM module
      statusEl.textContent = 'Loading WASM module...';
      if (!wasmModule) {
        // Dynamic import using indirect eval to avoid TypeScript module resolution
        const importDynamic = new Function('path', 'return import(path)');
        const wasm = (await importDynamic('/wasm/wasm_proto.js')) as {
          default: (path: string) => Promise<void>;
          decode_blueprints: (data: Uint8Array) => number;
        };
        await wasm.default('/wasm/wasm_proto_bg.wasm');
        wasmModule = wasm;
      }

      // Warm up (pre-fetch once to get files in browser cache)
      statusEl.textContent = 'Warming up...';
      await fetch('/data/blueprints.json');
      await fetch('/data/blueprints.pb');

      // Run JSON benchmark
      statusEl.textContent = `Running JSON benchmark (${iterations} iterations)...`;
      const jsonResults = await benchmarkJSON(iterations);

      // Run Proto reflection benchmark
      statusEl.textContent = `Running Proto (reflection) benchmark (${iterations} iterations)...`;
      const protoReflectionResults = await benchmarkProtoReflection(iterations);

      // Run Proto static benchmark
      statusEl.textContent = `Running Proto (static) benchmark (${iterations} iterations)...`;
      const protoStaticResults = await benchmarkProtoStatic(iterations);

      // Run Proto WASM benchmark
      statusEl.textContent = `Running Proto (WASM/Rust) benchmark (${iterations} iterations)...`;
      const protoWasmResults = await benchmarkProtoWasm(iterations);

      // Calculate fastest (min) times
      const jsonBest = {
        fetch: min(jsonResults, 'fetchTime'),
        parse: min(jsonResults, 'parseTime'),
        total: min(jsonResults, 'totalTime'),
        size: jsonResults[0].dataSize,
        count: jsonResults[0].itemCount,
      };

      const protoReflBest = {
        fetch: min(protoReflectionResults, 'fetchTime'),
        parse: min(protoReflectionResults, 'parseTime'),
        total: min(protoReflectionResults, 'totalTime'),
        size: protoReflectionResults[0].dataSize,
        count: protoReflectionResults[0].itemCount,
      };

      const protoStaticBest = {
        fetch: min(protoStaticResults, 'fetchTime'),
        parse: min(protoStaticResults, 'parseTime'),
        total: min(protoStaticResults, 'totalTime'),
        size: protoStaticResults[0].dataSize,
        count: protoStaticResults[0].itemCount,
      };

      const protoWasmBest = {
        fetch: min(protoWasmResults, 'fetchTime'),
        parse: min(protoWasmResults, 'parseTime'),
        total: min(protoWasmResults, 'totalTime'),
        size: protoWasmResults[0].dataSize,
        count: protoWasmResults[0].itemCount,
      };

      // Find fastest parse time
      const fastestParse = Math.min(
        jsonBest.parse,
        protoReflBest.parse,
        protoStaticBest.parse,
        protoWasmBest.parse
      );
      const fastestTotal = Math.min(
        jsonBest.total,
        protoReflBest.total,
        protoStaticBest.total,
        protoWasmBest.total
      );

      function parseClass(val: number) {
        return val === fastestParse ? 'result-value' : '';
      }
      function totalClass(val: number) {
        return val === fastestTotal ? 'result-value' : '';
      }

      // Display results
      resultsEl.innerHTML = `
        <h2>Results (best of ${iterations} iterations)</h2>
        <table>
          <tr>
            <th>Metric</th>
            <th>JSON</th>
            <th>Proto (reflection)</th>
            <th>Proto (static)</th>
            <th>Proto (WASM/Rust)</th>
          </tr>
          <tr>
            <td>File Size</td>
            <td>${formatSize(jsonBest.size)}</td>
            <td>${formatSize(protoReflBest.size)}</td>
            <td>${formatSize(protoStaticBest.size)}</td>
            <td>${formatSize(protoWasmBest.size)}</td>
          </tr>
          <tr>
            <td>Fetch Time (best)</td>
            <td>${formatMs(jsonBest.fetch)}</td>
            <td>${formatMs(protoReflBest.fetch)}</td>
            <td>${formatMs(protoStaticBest.fetch)}</td>
            <td>${formatMs(protoWasmBest.fetch)}</td>
          </tr>
          <tr>
            <td>Parse Time (best)</td>
            <td class="${parseClass(jsonBest.parse)}">${formatMs(jsonBest.parse)}</td>
            <td class="${parseClass(protoReflBest.parse)}">${formatMs(protoReflBest.parse)}</td>
            <td class="${parseClass(protoStaticBest.parse)}">${formatMs(protoStaticBest.parse)}</td>
            <td class="${parseClass(protoWasmBest.parse)}">${formatMs(protoWasmBest.parse)}</td>
          </tr>
          <tr>
            <td><strong>Total Time (best)</strong></td>
            <td class="${totalClass(jsonBest.total)}"><strong>${formatMs(jsonBest.total)}</strong></td>
            <td class="${totalClass(protoReflBest.total)}"><strong>${formatMs(protoReflBest.total)}</strong></td>
            <td class="${totalClass(protoStaticBest.total)}"><strong>${formatMs(protoStaticBest.total)}</strong></td>
            <td class="${totalClass(protoWasmBest.total)}"><strong>${formatMs(protoWasmBest.total)}</strong></td>
          </tr>
          <tr>
            <td>Items Parsed</td>
            <td>${jsonBest.count}</td>
            <td>${protoReflBest.count}</td>
            <td>${protoStaticBest.count}</td>
            <td>${protoWasmBest.count}</td>
          </tr>
        </table>

        <div class="comparison">
          <h3>Summary</h3>
          <p>Parse times relative to JSON:</p>
          <ul>
            <li>JSON: ${formatMs(jsonBest.parse)} (baseline)</li>
            <li>Proto (reflection): ${formatMs(protoReflBest.parse)} (${((protoReflBest.parse / jsonBest.parse) * 100).toFixed(0)}% of JSON)</li>
            <li>Proto (static): ${formatMs(protoStaticBest.parse)} (${((protoStaticBest.parse / jsonBest.parse) * 100).toFixed(0)}% of JSON)</li>
            <li>Proto (WASM/Rust): ${formatMs(protoWasmBest.parse)} (${((protoWasmBest.parse / jsonBest.parse) * 100).toFixed(0)}% of JSON)</li>
          </ul>
          <p style="margin-top: 10px;">File size: Proto is ${((1 - protoStaticBest.size / jsonBest.size) * 100).toFixed(1)}% smaller than JSON</p>
        </div>

        <h3 style="margin-top: 20px;">Raw Data</h3>
        <details>
          <summary style="cursor: pointer; color: #888;">JSON iterations</summary>
          <pre style="font-size: 0.8rem; margin-top: 10px;">${JSON.stringify(jsonResults, null, 2)}</pre>
        </details>
        <details>
          <summary style="cursor: pointer; color: #888;">Proto (reflection) iterations</summary>
          <pre style="font-size: 0.8rem; margin-top: 10px;">${JSON.stringify(protoReflectionResults, null, 2)}</pre>
        </details>
        <details>
          <summary style="cursor: pointer; color: #888;">Proto (static) iterations</summary>
          <pre style="font-size: 0.8rem; margin-top: 10px;">${JSON.stringify(protoStaticResults, null, 2)}</pre>
        </details>
        <details>
          <summary style="cursor: pointer; color: #888;">Proto (WASM/Rust) iterations</summary>
          <pre style="font-size: 0.8rem; margin-top: 10px;">${JSON.stringify(protoWasmResults, null, 2)}</pre>
        </details>
      `;

      statusEl.textContent = 'Benchmark complete!';
    } catch (e) {
      statusEl.textContent = `Error: ${e}`;
      console.error(e);
    } finally {
      runBtn.disabled = false;
    }
  });
}
